import ky from "ky";
import { toAxiosLikeError } from "./kyFetch";
import { notifyUnauthorizedResponse } from "@features/auth/authSessionHandler";

function buildKyOptions(method, config = {}) {
  const {
    data,
    params,
    headers,
    timeout,
    retry,
    hooks,
    throwHttpErrors,
    signal,
  } = config;

  const kyOpts = {
    method: method.toUpperCase(),
    retry: 0,
    timeout: false,
  };
  if (timeout !== undefined) kyOpts.timeout = timeout;
  if (retry !== undefined) kyOpts.retry = retry;
  if (hooks !== undefined) kyOpts.hooks = hooks;
  if (throwHttpErrors !== undefined) kyOpts.throwHttpErrors = throwHttpErrors;
  if (signal !== undefined) kyOpts.signal = signal;
  if (headers) kyOpts.headers = { ...headers };

  if (params != null) {
    kyOpts.searchParams = params;
  }

  const skipBody = kyOpts.method === "GET" || kyOpts.method === "HEAD";
  if (!skipBody && data !== undefined && data !== null) {
    if (typeof FormData !== "undefined" && data instanceof FormData) {
      kyOpts.body = data;
      if (kyOpts.headers) {
        const h = { ...kyOpts.headers };
        delete h["Content-Type"];
        delete h["content-type"];
        kyOpts.headers = h;
      }
    } else if (
      typeof URLSearchParams !== "undefined" &&
      data instanceof URLSearchParams
    ) {
      kyOpts.body = data;
    } else if (typeof Blob !== "undefined" && data instanceof Blob) {
      kyOpts.body = data;
    } else if (typeof data === "string") {
      kyOpts.body = data;
    } else {
      kyOpts.json = data;
    }
  }

  return kyOpts;
}

async function parseSuccessResponse(res, responseType) {
  if (responseType === "blob") {
    const data = await res.blob();
    return {
      data,
      status: res.status,
      statusText: res.statusText,
      headers: res.headers,
    };
  }
  const text = await res.text();
  if (text === "") {
    return {
      data: null,
      status: res.status,
      statusText: res.statusText,
      headers: res.headers,
    };
  }
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  if (ct.includes("application/json")) {
    try {
      return {
        data: JSON.parse(text),
        status: res.status,
        statusText: res.statusText,
        headers: res.headers,
      };
    } catch(err) {
      console.log("Error parsing json", err);
      return {
        data: text,
        status: res.status,
        statusText: res.statusText,
        headers: res.headers,
      };
    }
  }
  if (/^\s*(\[|\{)/.test(text)) {
    try {
      return {
        data: JSON.parse(text),
        status: res.status,
        statusText: res.statusText,
        headers: res.headers,
      };
    } catch(err) {
      console.log("Error parsing json", err);
      /* fall through */
    }
  }
  return {
    data: text,
    status: res.status,
    statusText: res.statusText,
    headers: res.headers,
  };
}

async function httpRequest(config) {
  const { url, responseType } = config;
  if (!url) {
    throw new Error("http: url is required");
  }
  const method = (config.method || "get").toLowerCase();
  const kyOpts = buildKyOptions(method, config);
  try {
    const res = await ky(url, kyOpts);
    const parsed = await parseSuccessResponse(res, responseType);
    return { ...parsed, config };
  } catch (err) {
    const wrapped = await toAxiosLikeError(err);
    notifyUnauthorizedResponse({
      url,
      status: wrapped.response?.status,
      code: wrapped.response?.data?.code,
      config,
    });
    throw wrapped;
  }
}

function httpGet(url, config = {}) {
  return httpRequest({ ...config, method: "get", url });
}

function httpPost(url, data, config = {}) {
  return httpRequest({ ...config, method: "post", url, data });
}

function httpPut(url, data, config = {}) {
  return httpRequest({ ...config, method: "put", url, data });
}

function httpPatch(url, data, config = {}) {
  return httpRequest({ ...config, method: "patch", url, data });
}

function httpDelete(url, config = {}) {
  return httpRequest({ ...config, method: "delete", url });
}

function httpCallable(config) {
  return httpRequest(config);
}

httpCallable.get = httpGet;
httpCallable.post = httpPost;
httpCallable.put = httpPut;
httpCallable.patch = httpPatch;
httpCallable.delete = httpDelete;
httpCallable.request = httpRequest;

export default httpCallable;
